/**
 * `peterhof_agent`'s questionnaire in the conversation: prior answers read as
 * visitor bubbles, the current question as a guide message, and its choices as
 * suggested replies. The store still owns the interrupt/resume contract.
 *
 * **Accessibility (§plan):** every option is a real `<button>`; focus moves
 * to the new question's heading on every step, so a screen reader announces
 * it and a sighted keyboard user does not have to hunt for where the card
 * jumped; Enter in the "свой вариант" field submits it (a plain `<form>`
 * `onSubmit`, no key handler needed).
 *
 * **An option with `input: 'date'` opens a calendar** («Другой день»,
 * 22.09.2026): a native `<input type="date">` between the option's own `min`
 * and `max`, and the picked day is answered as the slot's text — an ISO date.
 *
 * **A `counts` question is a stepper** («кто едет» by the museum's tariffs,
 * 24.09.2026): one row per option with −/+ buttons (`aria-label` names the
 * option), «Дальше» sends each id once per person and is disabled while nobody
 * is counted.
 *
 * **A question with `preset` opens filled in** (#112, INV-PH-57): the stepper
 * starts at the proposed counts and a single choice marks the proposed option; a
 * line under the question says so. The visitor's tap is still the answer.
 *
 * **«Выйти из анкеты» is always visible** and sends a fixed phrase — the
 * `resume {status:"exited", text}` contract wants the visitor's own words,
 * and a button press has none, so it stands in with a plain sentence a
 * transcript reads naturally either way.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { stripEmoji as plain } from '../lib/emoji';
import {
  COUNT_MAX,
  answerLabel,
  markedOption,
  useQuestionnaire,
  questionCount,
  visibleQuestions,
  summarizeAnswers,
  type Active,
  type Question,
  type ResumeValue,
} from '../store/questionnaireStore';

/** No typed text behind it — the fixed phrase a tap on «Выйти из анкеты» sends. */
export const LEAVE_PHRASE = 'Не сейчас, хочу спросить о другом.';

/** Only completed, still visible steps become earlier chat exchanges. */
export function questionChatHistory(active: Active): { question: Question; reply: string }[] {
  const visible = visibleQuestions(active);
  const current = visible.findIndex((item) => item.slot === active.currentSlot);
  return visible.slice(0, current).flatMap((question) => {
    const answer = active.answers[question.slot];
    if (answer === undefined && !active.skipped.includes(question.slot)) return [];
    return [{ question, reply: answer === undefined ? 'Пропустить' : answerLabel(question, answer) }];
  });
}

export default function QuestionCard({
  onComplete,
  onExit,
}: {
  /** `resume` is what to send; `summary` is the visitor bubble to show first. */
  onComplete: (resume: ResumeValue, summary: string) => void;
  onExit: (text: string, answered?: Record<string, string[] | string>, skipped?: string[]) => void;
}) {
  const active = useQuestionnaire((state) => state.active);
  const toggleMultiOption = useQuestionnaire((state) => state.toggleMultiOption);
  const setCustomText = useQuestionnaire((state) => state.setCustomText);
  const chooseSingle = useQuestionnaire((state) => state.chooseSingle);
  const submitMulti = useQuestionnaire((state) => state.submitMulti);
  const stepCount = useQuestionnaire((state) => state.stepCount);
  const submitCounts = useQuestionnaire((state) => state.submitCounts);
  const submitCustom = useQuestionnaire((state) => state.submitCustom);
  const skipCurrent = useQuestionnaire((state) => state.skipCurrent);
  const back = useQuestionnaire((state) => state.back);
  const close = useQuestionnaire((state) => state.close);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** The date option whose calendar is open on this step, if any. */
  const [picking, setPicking] = useState<string | null>(null);
  const [picked, setPicked] = useState('');

  // Hooks run unconditionally — the `if (!active)` bailout below has to come
  // after every one of them, or a later step (once `active` exists) would
  // change how many hooks this component calls.
  /** When this step appeared — a click that soon after is the tail of the previous step's double click. */
  const shownAt = useRef(0);
  const [badDate, setBadDate] = useState(false);

  useEffect(() => {
    if (active) headingRef.current?.focus();
    setPicking(null);
    setPicked('');
    setBadDate(false);
    shownAt.current = Date.now();
  }, [active?.currentSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;
  const visible = visibleQuestions(active);
  const index = visible.findIndex((q) => q.slot === active.currentSlot);
  const question = visible[index];
  if (!question) return null;
  /** The single choice's marked option: the visitor's earlier answer, else the proposal. */
  const marked = markedOption(question, active.answers[question.slot]);

  function land(resume: ResumeValue | null): void {
    if (!resume) return;
    const summary = resume.status === 'completed' ? summarizeAnswers(active!.questions, resume.answers) : '';
    close();
    onComplete(resume, summary);
  }

  /** Opens (or closes) an option's calendar — on its `default` day when the model
   *  proposed one (#149), so a visitor who named a Saturday does not scroll to it. */
  function openCalendar(option: { id: string; default?: string }): void {
    if (picking === option.id) {
      setPicking(null);
      return;
    }
    setPicking(option.id);
    setPicked((current) => calendarValue(current, option));
  }

  function exit(): void {
    const answered = active?.answers; // the questions already answered go with the exit (QA dev 30.10.2026, С3-3)
    const skipped = active?.skipped;
    close();
    onExit(LEAVE_PHRASE, answered, skipped);
  }

  return (
    <div
      className="question-chat"
      role="group"
      aria-label={plain(active.title ?? 'Вопросы гида')}
      onClickCapture={(event) => {
        // A double click on «Дальше»: its second click landed on the next question's first option and answered
        // it (first-user QA, dev, 21.10.2026). Two quick «+» on one step are not affected — the step did not change.
        if (isStepEcho(shownAt.current, Date.now())) {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
    >
      {questionChatHistory(active).map(({ question: previous, reply }) => (
        <div className="question-chat__exchange" key={previous.slot}>
          <div className="question-chat__prompt">{plain(previous.text)}</div>
          <div className="question-chat__reply">{plain(reply)}</div>
        </div>
      ))}
      <div className="question-chat__current">
        <div className="question-chat__meta">
          {active.title ? `${plain(active.title)} · ` : ''}Вопрос {index + 1} из {questionCount(active)}
        </div>
        <h3 className="question-card__text" tabIndex={-1} ref={headingRef}>
          {plain(question.text)}
        </h3>
        {question.preset !== undefined ? (
          <p className="question-card__hint" id={`hint-${question.slot}`}>
            {presetHint(question)}
          </p>
        ) : null}
      </div>

      {question.counts ? (
        <CountOptions question={question} onStep={stepCount} onNext={() => land(submitCounts())} />
      ) : question.multi ? (
        <MultiOptions question={question} onToggle={toggleMultiOption} onNext={() => land(submitMulti())} />
      ) : (
        <ul className="question-card__options">
          {question.options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className={cn(
                  'chip', 'question-card__option',
                  picking === option.id && 'question-card__option--checked',
                  marked === option.id && 'question-card__option--proposed',
                )}
                aria-describedby={marked === option.id && question.preset !== undefined ? `hint-${question.slot}` : undefined}
                aria-expanded={option.input === 'date' ? picking === option.id : undefined}
                onClick={() =>
                  option.input === 'date'
                    ? openCalendar(option)
                    : land(chooseSingle(option.id))
                }
              >
                {plain(option.label)}
              </button>
            </li>
          ))}
        </ul>
      )}

      {picking
        ? (() => {
            const option = question.options.find((o) => o.id === picking);
            return (
              <form
                className="question-card__custom question-card__date"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!picked) return;
                  setCustomText(picked);
                  land(submitCustom());
                }}
              >
                <input
                  type="date"
                  className="question-card__custom-input"
                  aria-label="Дата визита"
                  min={option?.min}
                  max={option?.max}
                  value={picked}
                  required
                  autoFocus
                  onChange={(event) => setPicked(event.target.value)}
                  // Chrome sends no `input` while the typed date stays invalid («31.02» keeps the value empty —
                  // round 2 of the first-user QA, 22.10.2026, saw no hint): read the field's validity as it is typed
                  onInput={(event) => setBadDate(event.currentTarget.validity.badInput)}
                  onKeyUp={(event) => setBadDate(event.currentTarget.validity.badInput)}
                  onBlur={(event) => setBadDate(event.currentTarget.validity.badInput)}
                />
                <button type="submit" className="btn btn--quiet" disabled={!picked}>
                  Ответить
                </button>
                {badDate ? <p className="question-card__hint">{BAD_DATE_HINT}</p> : null}
                {option?.default && picked === option.default ? (
                  <p className="question-card__hint" id={`hint-${option.id}`}>
                    {CALENDAR_HINT}
                  </p>
                ) : null}
              </form>
            );
          })()
        : null}

      {question.allow_custom ? (
        <form
          className="question-card__custom"
          onSubmit={(event) => {
            event.preventDefault();
            land(submitCustom());
          }}
        >
          <input
            type="text"
            className="question-card__custom-input"
            placeholder="Свой вариант"
            onChange={(event) => setCustomText(event.target.value)}
          />
          <button type="submit" className="btn btn--quiet">
            Ответить
          </button>
        </form>
      ) : null}

      <div className="question-card__foot">
        {index > 0 ? (
          <button type="button" className="chip question-card__action" onClick={back}>
            Назад
          </button>
        ) : null}
        {question.skippable ? (
          <button type="button" className="chip question-card__action" onClick={() => land(skipCurrent())}>
            Пропустить
          </button>
        ) : null}
        <button type="button" className="chip question-card__action question-card__leave" onClick={exit}>
          Спросить о другом
        </button>
      </div>
    </div>
  );
}

/** Under a calendar opened on the proposed day (#149): the field already holds it. */
export const CALENDAR_HINT = 'Предзаполнено — проверьте дату и нажмите «Ответить».';

/**
 * The calendar's value when it opens (#149, lead on review): what the visitor already
 * picked stays; otherwise the proposed day (`default`, sent only with a proposal);
 * otherwise empty — a date field's value IS an answer, so nothing is pre-filled that
 * nobody named.
 */
export function calendarValue(current: string, option: { default?: string }): string {
  return current || option.default || '';
}

/** The line under a question the card opened filled in. */
export function presetHint(question: Question): string {
  // Neutral on purpose (review of #112): the proposal is the model's reading, and
  // «по вашим словам» would put it in the visitor's mouth.
  return question.counts || question.multi
    ? 'Предзаполнено — проверьте и нажмите «Дальше».'
    : 'Отмечен вариант — нажмите, чтобы подтвердить, или выберите другой.';
}

/** A click this soon after a step appeared belongs to the step before it (a double click's tail). */
export const STEP_GRACE_MS = 350;

export function isStepEcho(shownAtMs: number, nowMs: number): boolean {
  return nowMs - shownAtMs < STEP_GRACE_MS;
}

/** «31.02» typed into the calendar: the field holds no date and «Ответить» was pale with no word why. */
export const BAD_DATE_HINT = 'Такой даты нет — выберите день в календаре.';

/** Under a «Дальше» that waits for an answer — the reason it is pale. */
export const COUNTS_EMPTY_HINT = 'Отметьте кнопкой «+», кто едет, — тогда «Дальше» заработает.';
export const MULTI_EMPTY_HINT = 'Выберите хотя бы один вариант — тогда «Дальше» заработает.';

function CountOptions({
  question,
  onStep,
  onNext,
}: {
  question: Question;
  onStep: (optionId: string, delta: number) => void;
  onNext: () => void;
}) {
  const counts = useQuestionnaire((state) => state.active?.draftCounts ?? {});
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  return (
    <>
      <ul className="question-card__options question-card__options--counts">
        {question.options.map((option) => {
          const n = counts[option.id] ?? 0;
          return (
            <li key={option.id} className="question-card__count">
              <span className="question-card__count-label">{plain(option.label)}</span>
              <button
                type="button"
                className="question-card__step"
                aria-label={`Меньше: ${plain(option.label)}`}
                disabled={n === 0}
                onClick={() => onStep(option.id, -1)}
              >
                −
              </button>
              <output className="question-card__count-value" aria-live="polite">
                {n}
              </output>
              <button
                type="button"
                className="question-card__step"
                aria-label={`Больше: ${plain(option.label)}`}
                disabled={n >= COUNT_MAX}
                onClick={() => onStep(option.id, 1)}
              >
                +
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="btn btn--primary" disabled={total === 0} onClick={onNext}>
        Дальше
      </button>
      {/* a pale «Дальше» that did nothing, with nothing saying why (first-user QA, dev, 21.10.2026). Its line
          keeps its place once answered: the card sits at the chat's end, and a line gone moved the steppers
          36 px down under a quick second «+» (round 2, 22.10.2026) */}
      <p className={cn('question-card__hint', total > 0 && 'question-card__hint--idle')} aria-hidden={total > 0}>
        {COUNTS_EMPTY_HINT}
      </p>
    </>
  );
}

function MultiOptions({
  question,
  onToggle,
  onNext,
}: {
  question: Question;
  onToggle: (optionId: string) => void;
  onNext: () => void;
}) {
  const picked = useQuestionnaire((state) => state.active?.draftMulti ?? []);
  return (
    <>
      <ul className="question-card__options question-card__options--multi">
        {question.options.map((option) => {
          const checked = picked.includes(option.id);
          return (
            <li key={option.id}>
              <button
                type="button"
                className={cn('question-card__option', checked && 'question-card__option--checked')}
                aria-pressed={checked}
                onClick={() => onToggle(option.id)}
              >
                {checked ? '✓ ' : ''}
                {plain(option.label)}
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="btn btn--primary" disabled={picked.length === 0} onClick={onNext}>
        Дальше
      </button>
      <p className={cn('question-card__hint', picked.length > 0 && 'question-card__hint--idle')} aria-hidden={picked.length > 0}>
        {MULTI_EMPTY_HINT}
      </p>
    </>
  );
}
